import { Image } from "@/components/image";
import { NoImage } from "@/components/no-image/NoImage";

type ImageWithFallbackProps = {
  /** When absent/empty, the NoImage placeholder is rendered instead. */
  src?: string;
  alt: string;
  width: number;
  height?: number;
  className?: string;
  fallback?: string;
  /**
   * Cache-busting param forwarded to <Image>. Only pass it where the original
   * call site already did — adding it changes the image URL.
   */
  crc?: string;
  priority?: boolean;
  /** Classes for the NoImage placeholder when they differ from `className`. */
  fallbackClassName?: string;
  /** Forwarded to <Image>: `contain` shows the whole product photo on a white mat. */
  fit?: "cover" | "contain";
};

/**
 * Mechanical replacement for the repo-wide ternary
 * `x?.URL ? <Image .../> : <NoImage .../>`. Renders <Image> when `src` is
 * truthy, otherwise the <NoImage> placeholder with the same width/height.
 */
export function ImageWithFallback({
  src,
  alt,
  width,
  height,
  className,
  fallback,
  crc,
  priority,
  fallbackClassName,
  fit,
}: ImageWithFallbackProps) {
  if (!src) {
    return (
      <NoImage
        width={width}
        height={height}
        className={fallbackClassName ?? className}
      />
    );
  }

  return (
    <Image
      src={src}
      alt={alt}
      width={width}
      height={height}
      className={className}
      fallback={fallback}
      crc={crc}
      priority={priority}
      fit={fit}
    />
  );
}
